import { jwtDecode } from "jwt-decode";
import { AccessTokenPayload } from "../models/AccessTokenPayload";
import { UserRole } from "../constants/UserRole";
import { GetApiBaseUrl } from "../utils/ConfigUtil";
import { ApiResponse } from "../models/ApiResponse";
import { TokenResponse } from "../models/TokenResponse";

export function GetApiAccessToken(): string | null {
  return localStorage.getItem("apiAccessToken");
}

export function SetApiAccessToken(accessToken: string): void {
  localStorage.setItem("apiAccessToken", accessToken);
}

export function RemoveApiAccessToken(): void {
  localStorage.removeItem("apiAccessToken");
}

// Refresh token is only sent in a response body if the backend has "SendRefreshTokenInCookie": false in appsettings.
// Only then it is available in JS. Otherwise, it's sent in cookies, and the browser handles it.
export function GetApiRefreshToken(): string | null {
  return localStorage.getItem("apiRefreshToken");
}

export function SetApiRefreshToken(refreshToken?: string): void {
  if (refreshToken) localStorage.setItem("apiRefreshToken", refreshToken);
}

export function RemoveApiRefreshToken(): void {
  localStorage.removeItem("apiRefreshToken");
}

export function SetToken(response: TokenResponse) {
  SetApiAccessToken(response.accessToken);
  SetApiRefreshToken(response.refreshToken);
}

export async function RefreshToken() {
  // attempt to refresh token if expired
  const refreshToken = GetApiRefreshToken();
  // const requestBody = JSON.stringify({ refreshToken: refreshToken });
  const apiBaseUrl: string = await GetApiBaseUrl();
  const tokenRefreshResponse = await fetch(
    apiBaseUrl + "api/Auth/RefreshToken",
    {
      method: "POST",
      // The following line would be required if the access token was contained in the cookies.
      // But in our case it produces an error, because the backend is not configured to receive
      // cross-site requests with credential cookies from the frontend
      // credentials: "include",
      body: JSON.stringify(refreshToken),
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
  if (!tokenRefreshResponse.ok) {
    return false;
  }
  const refreshResponse: ApiResponse<TokenResponse> =
    await tokenRefreshResponse.json();
  if (!refreshResponse.ok) {
    return false;
  }

  SetToken(refreshResponse.content);
  return true;
}

export function GetAccessTokenPayload(): AccessTokenPayload | null {
  const accessToken = GetApiAccessToken();
  if (!accessToken) return null;

  return jwtDecode<AccessTokenPayload>(accessToken);
}

export async function IsUserAuthenticated(): Promise<boolean> {
  const nowMs = new Date().getTime();

  const accessValid = IsAccessTokenValid(nowMs);
  if (accessValid) return true;

  const refreshValid = IsRefreshTokenValid(nowMs);
  if (!refreshValid) {
    return clearLogin();
  }

  const refreshSuccess = await RefreshToken();
  if (!refreshSuccess) {
    return clearLogin();
  }

  return true;
}

function IsAccessTokenValid(nowMs: number): boolean {
  const accessToken = GetApiAccessToken();
  if (!accessToken) return false;

  const { exp } = jwtDecode(accessToken);
  if (!exp) return true;

  return nowMs < exp * 1000;
}

function IsRefreshTokenValid(nowMs: number): boolean {
  const refreshToken = GetApiRefreshToken();
  if (!refreshToken) return false;

  const { exp } = jwtDecode(refreshToken);
  if (!exp) return true;

  return nowMs < exp * 1000;
}

function clearLogin() {
  RemoveApiAccessToken();
  RemoveApiRefreshToken();
  return false;
}

export function GetUserRoles(): UserRole[] {
  const accessTokenDecoded = GetAccessTokenPayload();
  if (!accessTokenDecoded) return [];

  if (typeof accessTokenDecoded.role === "string") {
    // happens when only one role is present
    return [accessTokenDecoded.role];
  }
  return accessTokenDecoded.role || [];
}

export function IsUserInternal(): boolean {
  return UserHasRoleOrHigher("Internal");
}

export function UserHasRoleOrHigher(role: UserRole): boolean {
  const userRoles = GetUserRoles();

  switch (role) {
    case "Partner":
      return userRoles.includes("Partner") || userRoles.includes("Admin");
    case "Internal":
      return (
        userRoles.includes("Internal") ||
        userRoles.includes("Manager") ||
        userRoles.includes("Admin")
      );
    case "Manager":
      return userRoles.includes("Manager") || userRoles.includes("Admin");
    case "Director":
      return userRoles.includes("Director")
    case "Admin":
      return userRoles.includes("Admin");
    default:
      return false;
  }
}

export function GetUserRoleAccess() {
  return {
    hasInternal: UserHasRoleOrHigher("Internal"),
    hasManager: UserHasRoleOrHigher("Manager"),
  };
}

export function GetUserPartnerId(): number | undefined {
  const accessTokenDecoded = GetAccessTokenPayload();
  return accessTokenDecoded?.PartnerId;
}

export function IsUserAdmin(): boolean {
  return UserHasRoleOrHigher("Admin");
}
